/**
 * @fileoverview Modal for displaying blurb generation prompts for debugging
 */

import { useState, Fragment } from 'react';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import {
  XMarkIcon,
  CodeBracketIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/**
 * Collapsible section component for displaying prompt content
 */
function PromptSection({ title, content, defaultOpen = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <Disclosure.Button className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left">
            <span className="font-medium text-gray-900 dark:text-gray-100">{title}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                    <span className="text-green-600 dark:text-green-400">Copied</span>
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy
                  </>
                )}
              </button>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </div>
          </Disclosure.Button>
          <Disclosure.Panel className="px-4 py-3 bg-white dark:bg-gray-800">
            <pre className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap font-mono overflow-x-auto max-h-96 overflow-y-auto">
              {content}
            </pre>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}

export default function BlurbPromptModal({
  isOpen,
  onClose,
  prompts = null,
  isLoading = false,
  error = null,
}) {
  const handleClose = () => {
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="flex items-center gap-2 text-base font-medium text-gray-900 dark:text-gray-100">
                    <CodeBracketIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    Blurb Generation Prompts
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
                  {/* Loading State */}
                  {isLoading && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400 mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Loading prompts...</p>
                    </div>
                  )}

                  {/* Error State */}
                  {error && !isLoading && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
                      <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                    </div>
                  )}

                  {/* Prompts Display */}
                  {!isLoading && !error && prompts && (
                    <div className="space-y-4">
                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300 pb-3 border-b border-gray-200 dark:border-gray-700">
                        <span>
                          <strong>Tone:</strong> {prompts.tone}
                        </span>
                        <span>
                          <strong>Category:</strong> {prompts.category}
                        </span>
                      </div>

                      {/* System Prompt */}
                      <PromptSection
                        title="System Prompt"
                        content={prompts.systemPrompt}
                        defaultOpen={true}
                      />

                      {/* User Prompt */}
                      <PromptSection
                        title="User Prompt"
                        content={prompts.userPrompt}
                        defaultOpen={true}
                      />

                      {/* Tone Instruction */}
                      <PromptSection
                        title="Tone Instruction"
                        content={prompts.toneInstruction}
                        defaultOpen={false}
                      />
                    </div>
                  )}

                  {/* No prompts yet */}
                  {!isLoading && !error && !prompts && (
                    <div className="text-center py-8">
                      <CodeBracketIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No prompts loaded yet
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
